import { useEffect, useRef } from 'react';
import { Track } from 'livekit-client';
import type { Room } from 'livekit-client';
import { applyConferenceCameraQuality } from '../utils/applyConferenceCameraQuality';
import { currentConferenceMediaTier } from '../utils/currentConferenceMediaTier';

function applyToSubscribedCameras(room: Room, galleryTileCount: number | null): void {
  const { tier } = currentConferenceMediaTier();
  room.remoteParticipants?.forEach((participant) => {
    participant.trackPublications.forEach((publication) => {
      if (publication.source === Track.Source.Camera && publication.isSubscribed) {
        applyConferenceCameraQuality(publication as any, tier, galleryTileCount);
      }
    });
  });
}

/**
 * SC-375: re-applies the camera cap to already-subscribed cameras when the gallery's tile count changes,
 * and once more as the gallery closes. New subscriptions get the right cap from the subscription loop,
 * which reads the same count. `galleryTileCount` is null while the gallery is not shown.
 */
export function useGalleryCameraQuality(room: Room | undefined, galleryTileCount: number | null): void {
  const wasGalleryRef = useRef(false);

  useEffect(() => {
    if (!room) return;
    const wasGallery = wasGalleryRef.current;
    wasGalleryRef.current = galleryTileCount != null;
    // Outside the gallery subscription owns the cap; only step in to restore it as the gallery closes.
    if (galleryTileCount == null && !wasGallery) return;
    applyToSubscribedCameras(room, galleryTileCount);
  }, [room, galleryTileCount]);
}
